/**
 * Copyright (c) Microsoft. All rights reserved.
 */

export type DisposableAdditionOptions = Partial<{
    ignoreAfterDisposal: boolean;
}>;

export type Disposable = () => void;

export type Lifetime = {
    /**
     * Adds a disposable to the life time.
     */
    add: (disposable: Disposable, options?: DisposableAdditionOptions) => void;
    /**
     * Checks if the life time is disposed.
     */
    isDisposed: boolean;
};

export type DisposableLifetime = Lifetime & {
    /**
     * Disposes the life time.
     */
    dispose: () => void;
};

/**
 * Creates a disposable life time that manages disposables.
 */
export const getLifetime = (): DisposableLifetime => {
    const disposables: Disposable[] = [];

    let isDisposed = false;

    const add = (disposable: Disposable, options?: DisposableAdditionOptions) => {
        const { ignoreAfterDisposal } = options || { ignoreAfterDisposal: false };

        if (isDisposed) {
            if (ignoreAfterDisposal) {
                return;
            }

            throw new Error('A lifetime cannot add disposables after being disposed. Create a new lifetime instead.');
        }

        disposables.push(disposable);
    };

    const dispose = () => {
        if (!isDisposed) {
            disposables.forEach(d => d());
            isDisposed = true;
        }
    };

    return {
        add,
        dispose,
        get isDisposed() {
            return isDisposed;
        }
    };
};
